import { VideoBackdrop } from '@solinkcorp/solink-ui/components/VideoBackdrop';
import React, { useEffect, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import { fitBox } from './letterbox';

/**
 * Track the intrinsic aspect ratio of a video element.
 *
 * @param video The element to observe, or null before a player has mounted one.
 * @returns width / height, or 0 while the dimensions are still unknown.
 */
function useVideoAspect(video: HTMLVideoElement | null): number {
  const [aspect, setAspect] = useState(0);

  useEffect(() => {
    if (!video) {
      setAspect(0);
      return;
    }

    const update = () => {
      const { videoWidth, videoHeight } = video;
      setAspect(videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : 0);
    };

    update();
    // `resize` fires when the stream changes resolution mid-playback.
    video.addEventListener('loadedmetadata', update);
    video.addEventListener('resize', update);
    return () => {
      video.removeEventListener('loadedmetadata', update);
      video.removeEventListener('resize', update);
    };
  }, [video]);

  return aspect;
}

interface LiveFeedStageProps {
  /** The element playing the visible feed. Its dimensions drive the letterbox. */
  videoElement: HTMLVideoElement | null;
  /** Rendered inside the fitted video rectangle. */
  children: React.ReactNode;
  /** Rendered above the video, e.g. the error overlay. */
  overlay?: React.ReactNode;
  /** Rendered over the whole stage, e.g. the camera carousel dots. */
  chrome?: React.ReactNode;
}

/**
 * Sizes a live feed to fit its widget while preserving the camera's aspect ratio, and fills the
 * leftover space with a dimmed backdrop sampled from that same feed.
 */
const LiveFeedStage: React.FC<LiveFeedStageProps> = ({
  videoElement,
  children,
  overlay,
  chrome,
}) => {
  const { width, height, observe } = useDimensions();
  const aspect = useVideoAspect(videoElement);

  // Before the first frame reports its dimensions there is nothing to letterbox, so the player
  // gets the whole widget — otherwise it would be sized to nothing and never report them.
  const layout = aspect
    ? fitBox(width, height, aspect)
    : { video: { left: 0, top: 0, width, height }, bars: [], orientation: 'none' as const };

  return (
    <div ref={observe} className="relative h-full w-full" data-testid="sdm-live-feed-stage">
      <VideoBackdrop
        bars={layout.bars}
        videoElement={videoElement}
        dimmed={!!overlay}
        testIdPrefix="sdm-live-feed"
      />
      <div className="absolute overflow-hidden" style={layout.video}>
        {children}
        {overlay}
      </div>
      {chrome}
    </div>
  );
};

export default LiveFeedStage;
